import styled from "styled-components";
import Button from "../Buttons/Button";
import IconButton from "../Buttons/IconButton";
import { pxToRem, withAlpha } from "../../styles/utils";

export const AssignmentsWrapper = styled.section`
  margin-top: ${({ theme }) => pxToRem(theme.spacing.xl)};
`;

export const AssignmentsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => pxToRem(theme.spacing.md)};
  margin-bottom: ${({ theme }) => pxToRem(theme.spacing.sm)};
`;

export const AssignmentsHeading = styled.h3`
  margin: 0;
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.lg)};
  font-weight: 600;
  line-height: 1.4;
`;

export const AddAssignmentButton = styled(Button)`
  width: auto;
  min-width: 0;
  max-width: none;
  height: ${({ theme }) => pxToRem(theme.controlHeight.sm)};
  padding: 0 ${({ theme }) => pxToRem(theme.spacing.md)};
  border: ${pxToRem(1)} solid ${({ theme }) => theme.borders.outline};
  border-radius: ${({ theme }) => pxToRem(theme.radii.md)};
  background: transparent;
  color: ${({ theme }) => theme.colors.ink};
  font-family: inherit;
  font-size: ${({ theme }) => pxToRem(theme.fontSize.md)};
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
  box-shadow: none;
  transition: background-color 0.15s ease, border-color 0.15s ease;

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.borders.outline};
    background: ${({ theme }) => theme.colors.inkSurface};
    color: ${({ theme }) => theme.colors.ink};
  }

  &:active:not(:disabled) {
    background: ${({ theme }) => theme.colors.inkSurface};
  }

  &:focus-visible {
    outline: ${pxToRem(2)} solid ${({ theme }) => theme.colors.brand};
    outline-offset: ${pxToRem(2)};
  }

  &:disabled {
    cursor: default;
    opacity: 0.45;
  }
`;

export const AssignmentsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => pxToRem(theme.spacing.sm)};
`;

export const Container = styled.article`
  padding: ${({ theme }) => pxToRem(theme.spacing.md)} ${({ theme }) => pxToRem(theme.spacing.lg)};
  border: ${pxToRem(1)} solid ${({ theme }) => theme.borders.outline};
  border-radius: ${({ theme }) => pxToRem(theme.radii.lg)};
  background: ${({ theme }) => theme.colors.white};
`;

export const AssignmentTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => pxToRem(theme.spacing.md)};
`;

export const AssignmentContentWrapper = styled.div`
  min-width: 0;
  flex: 1 1 auto;
`;

export const AssignmentsTitle = styled.h4`
  margin: 0;
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.md)};
  font-weight: 500;
  line-height: 1.45;
`;

export const AssignmentsDescription = styled.p`
  margin: ${({ theme }) => pxToRem(theme.spacing.xxs)} 0 0;
  color: ${({ theme }) => withAlpha(theme.colors.ink, 0.64)};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.md)};
  font-weight: 400;
  line-height: 1.5;
  white-space: pre-wrap;
`;

export const AssignmentMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => pxToRem(theme.spacing.xs)};
  margin-top: ${({ theme }) => pxToRem(theme.spacing.sm)};
`;

export const AssignmentTag = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => pxToRem(theme.spacing.xxs)};
  max-width: ${pxToRem(300)};
  height: ${pxToRem(30)};
  padding: 0 ${({ theme }) => pxToRem(theme.spacing.xs)};
  overflow: hidden;
  border-radius: ${({ theme }) => pxToRem(theme.radii.sm)};
  background: ${({ theme }) => theme.colors.inkSurface};
  color: ${({ theme }) => withAlpha(theme.colors.ink, 0.64)};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  font-weight: 400;
  line-height: 1;
  text-decoration: none;
  white-space: nowrap;

  &[href]:hover {
    color: ${({ theme }) => theme.colors.brand};
  }

  &:focus-visible {
    outline: ${pxToRem(2)} solid ${({ theme }) => theme.colors.brand};
    outline-offset: ${pxToRem(2)};
  }

  svg {
    width: ${pxToRem(14)};
    height: ${pxToRem(18)};
    flex: 0 0 auto;
    color: ${({ theme }) => theme.colors.brand};
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const ActionButtonsWrapper = styled.div`
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: ${({ theme }) => pxToRem(theme.spacing.xxs)};
`;

export const AssignmentIconButton = styled(IconButton)<{ $danger?: boolean }>`
  width: ${({ theme }) => pxToRem(theme.controlHeight.sm)};
  min-width: ${({ theme }) => pxToRem(theme.controlHeight.sm)};
  height: ${({ theme }) => pxToRem(theme.controlHeight.sm)};
  padding: 0;
  border: 0;
  border-radius: ${({ theme }) => pxToRem(theme.radii.md)};
  background: transparent;
  color: ${({ theme }) => withAlpha(theme.colors.ink, 0.42)};
  transform: none;

  &:hover {
    background: ${({ $danger, theme }) =>
      $danger ? withAlpha(theme.colors.dangerText, 0.08) : theme.colors.inkSurface};
    color: ${({ $danger, theme }) => ($danger ? theme.colors.dangerText : theme.colors.ink)};
    transform: none;
  }

  &:focus-visible {
    outline: ${pxToRem(2)} solid ${({ theme }) => theme.colors.brand};
    outline-offset: ${pxToRem(2)};
  }

  svg {
    width: ${pxToRem(20)};
    height: ${pxToRem(20)};
  }
`;

export const EmptyAssignments = styled.div`
  padding: ${({ theme }) => pxToRem(theme.spacing.lg)};
  border: ${pxToRem(1)} dashed ${({ theme }) => withAlpha(theme.colors.ink, 0.26)};
  border-radius: ${({ theme }) => pxToRem(theme.radii.lg)};
  color: ${({ theme }) => withAlpha(theme.colors.ink, 0.42)};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.md)};
  font-weight: 400;
  line-height: 1.5;
  text-align: center;
`;

export const LoadingText = styled.div`
  padding: ${({ theme }) => pxToRem(theme.spacing.sm)} 0;
  color: ${({ theme }) => withAlpha(theme.colors.ink, 0.42)};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.md)};
`;
